import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import {
  DrawerContent,
  DrawerTopBar,
  PersistentDrawer,
  SwipeableDrawer,
} from "@/components/Drawer";
import { TravellerFormDrawer } from "@/features/travellers/components/TravellerFormDrawer";
import { TravellerSelectionStatusDrawer } from "@/features/travellers/components/TravellerSelectionStatusDrawer";
import { TravellersList } from "@/features/travellers/components/TravellersList";
import { TravellersSelection } from "@/features/travellers/components/TravellersSelection";
import {
  TravellersSelectionProvider,
  useTravellersSelectionContext,
} from "@/features/travellers/contexts/TravellersSelectionContext";
import { travellersQueryOptions } from "@/features/travellers/hooks/useTravellers";
import type { TravellerAgeGroup } from "@/features/travellers/types/types";
import { getTravellerTypeAtTravelDate } from "@/features/travellers/utils/ageValidation";
import { travelerTypeToTypeCode } from "@/features/travellers/utils/transformTraveler";
import type { BookingData } from "@/features/trips/types/types";

export const Route = createFileRoute(
  "/_authenticated/trips/$tripId/travellers",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { getValues } = useFormContext<BookingData>();
  const tripDateString = getValues("tripDate");
  const selectedTravellers = getValues("selectedTravellers");

  return (
    <TravellersSelectionProvider
      initialTravellersSlots={selectedTravellers}
      travelDate={new Date(tripDateString)}
    >
      <RouteComponentInner />
    </TravellersSelectionProvider>
  );
}

function RouteComponentInner() {
  const intl = useIntl();
  const navigate = Route.useNavigate();
  const { data: travellersData } = useQuery(travellersQueryOptions());

  const { setValue } = useFormContext<BookingData>();
  const tripDateString = useWatch<BookingData, "tripDate">({
    name: "tripDate",
  });
  const tripDate = new Date(tripDateString);
  const { travellerSlots, selectedTravellers, selectTraveller } =
    useTravellersSelectionContext();
  const [selectedSlot, setSelectedSlot] = useState<{
    ageGroup: TravellerAgeGroup;
    index: number;
  } | null>(null);

  const travellersToSelect =
    travellerSlots.ADT.length +
    travellerSlots.CHD.length +
    travellerSlots.INF.length;

  const allTravellersSelected =
    travellerSlots.ADT.every(Boolean) &&
    travellerSlots.CHD.every(Boolean) &&
    travellerSlots.INF.every(Boolean);

  return (
    <>
      <h2 className="text-xl font-medium text-text-title">
        {intl.formatMessage({
          defaultMessage: "Enter traveller details",
          description: "Page heading for the trip travellers selection page",
        })}
      </h2>
      <p className="mb-4 text-sm text-text-tertiary">
        {intl.formatMessage({
          defaultMessage: "Tap each traveller below to enter their information",
          description:
            "Instruction to tap each traveller card to enter their information",
        })}
      </p>

      <TravellersSelection
        onSlotClick={(ageGroup, index) => {
          setSelectedSlot({ ageGroup, index });
        }}
      />

      {selectedSlot ? (
        travellersData?.length ? (
          <SwipeableDrawer
            hasFullHeight
            open={!!selectedSlot}
            onClose={() => setSelectedSlot(null)}
            backgroundColor="background"
          >
            <DrawerTopBar
              onCloseClick={() => setSelectedSlot(null)}
              title={
                selectedSlot
                  ? intl.formatMessage(
                      {
                        defaultMessage: "Add traveller for {type}",
                        description:
                          "Prefix label when assigning a traveller to a booking slot",
                      },
                      {
                        type: intl.formatMessage(
                          {
                            defaultMessage:
                              "{type, select, ADT {Adult} CHD {Child} INF {Infant} other {Traveller}} {number}",
                            description:
                              "Numbered traveller slot label (e.g. 'Adult 1', 'Child 2')",
                          },
                          {
                            type: selectedSlot.ageGroup,
                            number: selectedSlot.index + 1,
                          },
                        ),
                      },
                    )
                  : null
              }
            />
            <DrawerContent>
              <TravellersList
                travellers={(travellersData || []).filter(
                  (traveller) =>
                    travelerTypeToTypeCode(
                      getTravellerTypeAtTravelDate(
                        traveller.birthDate,
                        tripDate,
                      ),
                    ) === selectedSlot.ageGroup,
                )}
                travelDate={tripDate}
                selectedTravellerId={
                  travellerSlots[selectedSlot.ageGroup][selectedSlot.index]
                }
                isSelectionDisabled={(traveller) =>
                  selectedTravellers.some((t) => t.id === traveller.id)
                }
                onTravellerSelected={(travellerId) => {
                  selectTraveller(selectedSlot.index, travellerId);
                  setSelectedSlot(null);
                }}
              />
            </DrawerContent>
          </SwipeableDrawer>
        ) : (
          <TravellerFormDrawer
            open
            traveller={null}
            onSubmitSuccess={(traveller) => {
              selectTraveller(selectedSlot.index, traveller.id);
            }}
            onClose={() => {
              setSelectedSlot(null);
            }}
          />
        )
      ) : null}

      <TravellerSelectionStatusDrawer
        travellersSelected={selectedTravellers.length}
        travellersToSelect={travellersToSelect}
      />

      <PersistentDrawer>
        <Button
          fullWidth
          variant="contained"
          disabled={!allTravellersSelected}
          onClick={() => {
            setValue("selectedTravellers", travellerSlots);
            navigate({ to: "../summary" });
          }}
        >
          {intl.formatMessage({
            defaultMessage: "Continue",
            description:
              "Button to proceed to the next step in the trip booking flow",
          })}
        </Button>
      </PersistentDrawer>
    </>
  );
}
